/**
 * InstagramPair.tsx
 *
 * File purpose:
 * - Render up to two Instagram embeds side-by-side using a polished Evy Baby style.
 * - Provide a graceful fallback card/thumbnail that links to the Instagram post(s).
 * - Support `forceFallback` to immediately show the fallback UI without attempting iframes.
 * - Support configurable visual size via `size` prop (width in px). Default matches Evy Baby: 350px.
 */

import React, { useMemo, useState } from 'react'
import { ExternalLink } from 'lucide-react'

/**
 * InstagramPairProps interface
 *
 * - srcs: array of Instagram embed/page URLs (up to 2 recommended)
 * - titles: optional titles for each item
 * - forceFallback: when true, the component immediately shows fallback cards
 * - size: width in pixels for each card (keeps Evy Baby tall aspect). Default 350
 * - className: optional additional classes for container
 */
export interface InstagramPairProps {
  srcs: string[]
  titles?: string[]
  forceFallback?: boolean
  size?: number
  className?: string
}

/**
 * InstagramPair
 *
 * Component:
 * - Renders 1 or 2 Instagram embed iframes with a safe sandbox.
 * - If `forceFallback` is true or an iframe fails to load, the component shows the fallback card(s).
 * - Visual style: Evy Baby — tall portrait cards, rounded, shadowed, hover scale, and bottom gradient bar.
 *
 * @param props InstagramPairProps
 * @returns JSX.Element
 */
export default function InstagramPair({
  srcs,
  titles = [],
  forceFallback = false,
  size = 350,
  className = ''
}: InstagramPairProps): JSX.Element {
  // Limit to two items
  const items = srcs.slice(0, 2)

  /**
   * failedInitial
   *
   * - Build initial failed state based on forceFallback.
   */
  const failedInitial = useMemo(() => items.map(() => !!forceFallback), [items.length, forceFallback])
  const [failed, setFailed] = useState<boolean[]>(failedInitial)

  /**
   * handleIframeError
   *
   * Marks the iframe at given index as failed to show the fallback UI.
   *
   * @param index index of the iframe that failed
   */
  const handleIframeError = (index: number) => {
    setFailed((prev) => {
      const copy = [...prev]
      copy[index] = true
      return copy
    })
  }

  /**
   * normalizeToPageUrl
   *
   * Convert an embed URL (ending with /embed or /embed/) to the regular Instagram page URL.
   *
   * @param embedUrl the embed URL
   * @returns normalized page URL
   */
  const normalizeToPageUrl = (embedUrl: string) => {
    try {
      return embedUrl.replace(/\/embed\/?$/, '').replace(/\/+$/, '')
    } catch {
      return embedUrl
    }
  }

  /**
   * buildDisplayLabel
   *
   * Create a compact label from the Instagram URL (username + path)
   *
   * @param url original URL
   * @returns short label for UI
   */
  const buildDisplayLabel = (url: string) => {
    try {
      const u = new URL(normalizeToPageUrl(url))
      const parts = u.pathname.split('/').filter(Boolean)
      if (parts.length === 0) return u.hostname
      if (parts.length >= 3 && (parts[1] === 'p' || parts[1] === 'reel' || parts[1] === 'reels')) {
        return `${parts[0]}/${parts[1]}/${parts[2]}`
      }
      return parts.join('/')
    } catch {
      return url
    }
  }

  /**
   * renderFallbackCard
   *
   * Build a card styled as a lightweight thumbnail which links to the Instagram post.
   * This avoids fragile iframe rendering while providing a clear CTA to open the post.
   *
   * @param src original embed URL
   * @param index index used for key
   */
  const renderFallbackCard = (src: string, index: number) => {
    const pageUrl = normalizeToPageUrl(src)
    const label = buildDisplayLabel(src)
    const title = titles[index] ?? 'Instagram post'

    return (
      <a
        key={`fallback-${index}`}
        href={pageUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="group block mx-auto rounded-xl overflow-hidden border bg-black border-gray-800 shadow-lg transform transition-transform hover:scale-[1.02]"
        aria-label={`Open ${title} on Instagram`}
        style={{
          width: size,
          maxWidth: '100%',
          aspectRatio: '350/630'
        }}
      >
        <div className="w-full h-full flex flex-col justify-between" style={{ background: 'linear-gradient(180deg,#0f172a,#0b1220)' }}>
          <div className="flex-1 flex items-center justify-center px-4">
            <div className="w-full h-full flex items-center justify-center text-gray-200">
              <div className="text-center px-4">
                <div className="mb-3 text-xs uppercase tracking-wider text-orange-400">Instagram</div>
                <div className="text-sm font-medium break-words">{title}</div>
                <div className="mt-2 text-xs text-gray-300">{label}</div>
              </div>
            </div>
          </div>

          <div className="p-3 border-t border-gray-800 bg-gradient-to-t from-black/30 to-transparent flex items-center justify-between">
            <span className="text-xs text-gray-200">Open on Instagram</span>
            <ExternalLink className="h-4 w-4 text-gray-200" />
          </div>
        </div>
      </a>
    )
  }

  /**
   * renderSingle
   *
   * Builds the iframe for a single embed. Falls back to the card when iframe throws error.
   *
   * @param src embed src
   * @param title optional title for the iframe
   * @param index iframe index
   */
  const renderSingle = (src: string, title?: string, index = 0) => {
    const key = `${src}-${index}`
    const pageUrl = normalizeToPageUrl(src)

    return (
      <div
        key={key}
        className="mx-auto rounded-xl overflow-hidden bg-black border border-gray-800 shadow-lg transform transition-transform hover:scale-[1.02]"
        style={{
          width: size,
          maxWidth: '100%',
          aspectRatio: '350/630',
          boxShadow: '0 10px 30px rgba(2,6,23,0.28)'
        }}
      >
        {!failed[index] ? (
          <>
            <iframe
              src={src}
              title={title ?? `instagram-embed-${index}`}
              className="w-full h-full pointer-events-auto"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              loading="lazy"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-presentation"
              style={{ border: 0, display: 'block', width: '100%', height: '100%', background: '#000' }}
              onError={() => handleIframeError(index)}
            />
            {/* Accessible overlay with link (visible on hover) */}
            <a
              href={pageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-x-0 bottom-0 z-10 pointer-events-auto"
              aria-hidden="true"
              onClick={(e) => {
                // allow clicking the overlay to open in new tab but don't stop iframe interactions
                // noop; anchor handles it
              }}
              style={{ textDecoration: 'none' }}
            >
              <div className="hidden group-hover:flex items-center justify-between px-3 py-2 bg-gradient-to-t from-black/70 to-transparent text-gray-100 rounded-b-xl">
                <div className="text-sm">{title ?? 'Instagram post'}</div>
                <ExternalLink className="h-4 w-4" />
              </div>
            </a>
          </>
        ) : (
          renderFallbackCard(src, index)
        )}
      </div>
    )
  }

  // Layout:
  // - On small screens stack items vertically so each iframe is displayed under another.
  // - On md+ screens keep the horizontal layout (side-by-side).
  // - Keep card sizes via inline style (width: size) while allowing maxWidth: '100%' for narrow viewports.
  return (
    <div className={`w-full ${className} flex flex-col md:flex-row items-center md:justify-center gap-4`}>
      {items.map((src, i) => {
        if (failed[i]) {
          return renderFallbackCard(src, i)
        }
        return renderSingle(src, titles[i], i)
      })}
    </div>
  )
}
